/**
 * Organization membership + invitations — request schemas, wire types and the
 * refusal codes (security package B, docs/USER_ROLES_AND_PERMISSIONS.md
 * "How membership is created", docs/BACKEND_API.md).
 *
 * Served by `apps/api/src/routes/platform/organizations.ts` and
 * `apps/api/src/routes/platform/invitations.ts` over migration
 * `20261103160000_membership_backend_writes_and_audit.sql`:
 *
 *   POST   /api/v1/organizations                                  → CreateOrganizationResponse (any signed-in user)
 *   GET    /api/v1/organizations/:orgId/members                   → OrganizationMembersResponse (members)
 *   PATCH  /api/v1/organizations/:orgId/members/:memberId         → ChangeMemberRoleResponse (org admins)
 *   DELETE /api/v1/organizations/:orgId/members/:memberId         → { ok: true } (org admins)
 *   PUT    /api/v1/organizations/:orgId/membership/starred        → SetStarredResponse (the caller's own row)
 *   GET    /api/v1/organizations/:orgId/invitations               → OrganizationInvitationsResponse (org admins)
 *   POST   /api/v1/organizations/:orgId/invitations               → CreateInvitationResponse (org admins)
 *   POST   /api/v1/organizations/:orgId/invitations/:id/resend    → ResendInvitationResponse (org admins)
 *   DELETE /api/v1/organizations/:orgId/invitations/:id           → { ok: true } (org admins)
 *   POST   /api/v1/invitations/:id/accept                         → AcceptInvitationResponse (the invitee)
 *   POST   /api/v1/invitations/:id/decline                        → { ok: true } (the invitee)
 *
 * The wire interfaces are shared by the API handlers and the web hooks so the
 * two sides cannot drift.
 */
import { z } from "zod";
import { ORG_ROLES } from "../orgRoles.js";
// ---------------------------------------------------------------------------
// Refusal codes
// ---------------------------------------------------------------------------
/**
 * The `code`s of the membership refusals (`{ error, code }`); the web keys its
 * toasts on these (`team.toasts.<camelCase key>`).
 *
 *   last_admin          409  RV035 — the change would leave no admin who can sign in
 *   self_change         409  a caller changing / removing their own membership
 *   admin_required      403  not an org admin (the API's `ADMIN_REQUIRED_CODE`)
 *   member_not_found    404  no such member in this organization
 *   invitation_not_found 404 no such invitation (in this org / addressed to the caller)
 *   invitation_invalid  409  already accepted, withdrawn, declined or expired
 *   already_member      409  inviting / accepting for someone who is already a member
 */
export const ORG_MEMBERSHIP_ERROR_CODES = {
    lastAdmin: "last_admin",
    selfChange: "self_change",
    adminRequired: "admin_required",
    memberNotFound: "member_not_found",
    invitationNotFound: "invitation_not_found",
    invitationInvalid: "invitation_invalid",
    alreadyMember: "already_member",
};
// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------
const orgRoleSchema = z.enum(ORG_ROLES);
/** PATCH …/members/:memberId */
export const changeMemberRoleBodySchema = z.object({ role: orgRoleSchema }).strict();
/** PUT …/membership/starred */
export const setOrganizationStarredBodySchema = z.object({ starred: z.boolean() }).strict();
/** POST …/invitations. The address is trimmed; the DB re-checks it. */
export const createInvitationBodySchema = z
    .object({
    email: z.string().trim().email().max(320),
    role: orgRoleSchema,
    keycloak_provisioning: z.boolean().optional(),
})
    .strict();
/** POST /api/v1/organizations (`create_organization_with_owner`; the timezone is checked in SQL). */
export const createOrganizationBodySchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    timezone: z.string().trim().min(1).max(100),
    description: z.string().max(2000).optional(),
})
    .strict();
//# sourceMappingURL=organizationMembers.js.map